import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { OptionCard } from '../components/OptionCard';
import { listProducts } from '../lib/api';
import { formatPrice, pickI18n } from '../lib/format';
import { useKiosk } from '../lib/session';

/**
 * 商品瀏覽 — 依 `?category=` 分流。
 *
 * 設計稿（p.5）把「搭船遊港」與「水上設施」拆成兩個入口，兩者共用這一頁，
 * 差別只在分類與標題：
 *   trip       → 搭船遊港（所有航程）
 *   experience → 水上設施（SUP、水上自行車…）
 * 不帶分類時列出全部，維持舊有的「我自己選」行為，供既有連結繼續運作。
 */
export function BrowseScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, locale, patchDraft } = useKiosk();
  const [params] = useSearchParams();
  const category = params.get('category') ?? undefined;

  const productsQuery = useQuery({
    // 分類是查詢的一部分，必須進 queryKey，否則切換入口會拿到上一個分類的快取
    queryKey: ['products', category ?? 'all'],
    queryFn: () => listProducts(category),
  });
  const items = (productsQuery.data?.items ?? []).filter((p) => p.is_published);

  return (
    <KioskFrame>
      <h1 className="k-h1">
        {category === 'experience' ? t('browse.titleWater') : t('browse.title')}
      </h1>

      {productsQuery.isPending && <p className="k-text">{t('common.loading')}</p>}
      {productsQuery.isError && <p className="k-error">{t('common.errNetwork')}</p>}
      {productsQuery.isSuccess && items.length === 0 && (
        <p className="k-text k-text--dim">{t('browse.empty')}</p>
      )}

      {items.map((p) => (
        <OptionCard
          key={p.id}
          title={pickI18n(p.name_i18n, p.sku, locale)}
          desc={
            p.duration_minutes !== null ? `${p.duration_minutes} ${t('browse.minutes')}` : undefined
          }
          trailing={
            <span style={{ fontSize: 'var(--fs-h2)', fontWeight: 800, color: 'var(--c-accent)' }}>
              {formatPrice(p.base_price, p.currency)}
            </span>
          }
          onClick={() => {
            const isPackage = p.category === 'package';
            patchDraft({
              product: p,
              schedule: null,
              plan: null,
              planTotal: null,
              // 套票不建班次，改走逐段選時段那一頁
              planProducts: isPackage ? [p] : null,
            });
            navigate(isPackage ? '/plan' : '/schedule');
          }}
        />
      ))}

      <div className="k-footbar">
        <KioskButton variant="secondary" onClick={() => navigate('/home')}>
          ← {t('common.back')}
        </KioskButton>
      </div>
    </KioskFrame>
  );
}
